"use client";

import { useEffect, useRef, useState } from "react";
import type { HfImage } from "../model";
import { CloseIcon, GridIcon } from "../icons";

const SHOWN = 5;

/**
 * The room's photos: one large and up to four small, each individually rounded. Any photo opens the full set in a modal
 * photo list; on a phone the row scrolls sideways with a "1 / 9" counter. Layout adapts to how many photos there are,
 * so one or two photos never leave empty tiles.
 */
export function RoomGallery({ photos, title, tiles = SHOWN }: { photos: HfImage[]; title: string; tiles?: number }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const limit = Math.min(SHOWN, Math.max(1, tiles));
  const shown = photos.slice(0, limit);

  useEffect(() => {
    const element = strip.current;
    if (!element) return;
    const update = () => {
      const first = element.firstElementChild as HTMLElement | null;
      if (!first) return;
      const step = first.getBoundingClientRect().width + 8;
      setCurrent(Math.min(shown.length - 1, Math.max(0, Math.round(element.scrollLeft / step))));
    };
    element.addEventListener("scroll", update, { passive: true });
    return () => element.removeEventListener("scroll", update);
  }, [shown.length]);

  function open(index: number) {
    const element = dialog.current;
    if (!element) return;
    element.showModal();
    const target = element.querySelector<HTMLElement>(`[data-photo="${index}"]`);
    target?.scrollIntoView({ block: "start" });
  }

  if (photos.length === 0) return null;
  return (
    <div className="hf-gal-wrap">
      <div className="hf-gal" data-count={shown.length} ref={strip}>
        {shown.map((photo, index) => (
          <figure key={photo.src + index} className={`hf-gal-${index}`}>
            <button type="button" className="hf-gal-open" onClick={() => open(index)} aria-label={`Open photo ${index + 1} of ${photos.length}`}>
              <img src={photo.src} srcSet={photo.srcSet} alt={photo.alt} loading={index === 0 ? "eager" : "lazy"} decoding="async" data-fade="" />
            </button>
            {index === shown.length - 1 && photos.length > shown.length ? (
              <button type="button" className="hf-gal-all" onClick={() => open(0)}>
                <GridIcon />
                <span>See all {photos.length} photos</span>
              </button>
            ) : null}
          </figure>
        ))}
      </div>
      {photos.length > 1 ? (
        <span className="hf-gal-count" aria-hidden="true">
          {current + 1} / {photos.length}
        </span>
      ) : null}
      <dialog ref={dialog} className="hf-viewer" aria-label={`Photos of ${title}`}>
        <div className="hf-viewer-bar">
          <button type="button" className="hf-btn hf-btn-tone hf-btn-icon" onClick={() => dialog.current?.close()} aria-label="Close photos" autoFocus>
            <CloseIcon />
          </button>
          <p className="hf-viewer-title">
            {title} <span>{photos.length} {photos.length === 1 ? "photo" : "photos"}</span>
          </p>
        </div>
        <div className="hf-viewer-list">
          {photos.map((photo, index) => (
            <figure key={photo.src + index} data-photo={index}>
              <img src={photo.src} srcSet={photo.srcSet} alt={photo.alt} loading="lazy" decoding="async" />
            </figure>
          ))}
        </div>
      </dialog>
    </div>
  );
}
